import {
  ExperimentClient as SDKExperimentClient,
  GatesService,
  TreatmentService,
  type Treatment,
  type ExperimentType,
  type Experiment,
  type Gate,
} from '@grammarly/experimentation-js-sdk'

import type { ServerService } from '@lib/services/server-data'
import { gnarClient } from '@lib/tracking/gnar'
import { Cookie, getCookie } from '@lib/utils/cookies'

import { APP_ENV, APP_VERSION, IS_BROWSER, SERVICE_NAME } from '@constants/globals'

import type { NavigationService } from '../navigation/navigation-service'

import { Experiments, ExperimentNamesSet } from './experiments'
import { Gates, GatesNameSet } from './gates'
import { ExperimentOverrides } from './overrides'
import { PixelExperiment } from './pixel-experiment'
import { type PixelExperimentTracker } from './pixel-tracker'
import type { TreatmentOrNotAssigned, TreatmentOrNotAssignedOrNull } from './types'

/**
 * ExperimentClient is responsible for parsing overrides and managing the ExperimentClient from the experimentation SDK.
 *
 * This client handles:
 * - Managing the SDK ExperimentClient instance
 * - Parsing experiment overrides from URL params and localStorage
 * - Providing a clean interface for the ExperimentService
 */
export class ExperimentClient {
  private static readonly TIMEOUT = 10 * 1000

  private readonly _experimentOverrides: ExperimentOverrides
  private readonly _sdkExperimentClient?: SDKExperimentClient
  private readonly _pixelTracker: PixelExperimentTracker

  private readonly _experimentsWithAssignedTreatment: Map<string, TreatmentOrNotAssigned<any>>

  constructor(
    fetchFn: typeof globalThis.fetch,
    shWebServerService: ServerService,
    pixelTracker: PixelExperimentTracker,
    navigationService?: NavigationService,
  ) {
    this._experimentOverrides = new ExperimentOverrides(navigationService)
    this._experimentsWithAssignedTreatment = new Map<string, TreatmentOrNotAssigned<any>>()
    this._pixelTracker = pixelTracker
    this._sdkExperimentClient = this.createSDKExperimentClient(fetchFn, shWebServerService)
  }

  /**
   * Get application request headers for SDK requests
   */
  private async getApplicationRequestHeaders() {
    const containerId = await gnarClient.getContainerId()

    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': IS_BROWSER ? getCookie(Cookie.CsrfToken) : undefined,
      'X-Client-Type': SERVICE_NAME,
      'X-Client-Version': APP_VERSION,
      'X-Container-Id': containerId,
    }
  }

  /**
   * Create the SDK ExperimentClient instance
   */
  private createSDKExperimentClient(
    fetchFn: typeof globalThis.fetch,
    shWebServerService: ServerService,
  ): SDKExperimentClient {
    const treatmentService = new TreatmentService(
      fetchFn,
      {
        getApplicationHeaders: this.getApplicationRequestHeaders.bind(this),
        timeout: ExperimentClient.TIMEOUT,
      },
      APP_ENV,
    )

    const gateService = new GatesService(
      fetchFn,
      {
        getApplicationHeaders: this.getApplicationRequestHeaders.bind(this),
        timeout: ExperimentClient.TIMEOUT,
      },
      APP_ENV,
    )

    const experimentClient = new SDKExperimentClient(
      treatmentService,
      Object.values(Experiments),
      gateService,
      Object.values(Gates),
      {
        ssrDisableLogging: !IS_BROWSER,
      },
    )

    const peekedTreatments: Treatment[] = shWebServerService.get().treatments ?? []
    const overrides: { [key: string]: string } = this._experimentOverrides.getExperimentOverrides()

    const treatmentsWithOverrides: Treatment[] = [
      ...Object.entries(overrides).map(([name, experienceName]) => {
        const experimentType = (GatesNameSet.has(name) ? 'gate' : 'experiment') as ExperimentType

        return {
          experimentName: name,
          groupName: `${experienceName}_1`,
          experimentId: 'dummy',
          type: experimentType,
        }
      }),
      ...peekedTreatments,
    ].filter(
      treatment =>
        GatesNameSet.has(treatment.experimentName) ||
        ExperimentNamesSet.has(treatment.experimentName),
    )

    experimentClient.setPrefetchedTreatments(treatmentsWithOverrides)

    return experimentClient
  }

  /**
   * Helper method to check if a treatment is valid for an experiment
   */
  private isValidTreatment<EX extends string>(
    experiment: Experiment<EX>,
    treatment: string,
  ): treatment is EX {
    return experiment.experiencesArray.includes(treatment as EX) || treatment === 'NOT_ASSIGNED'
  }

  /**
   * Helper method to check if a treatment is valid for a gate
   */
  private isValidGateTreatment<EX extends string>(
    experiment: Gate<EX>,
    treatment: string,
  ): treatment is EX {
    return experiment.experience === (treatment as EX) || treatment === 'NOT_ASSIGNED'
  }

  /**
   * Get override treatment for Statsig experiments
   */
  private getOverrideTreatment<EX extends string>(
    experiment: Experiment<EX>,
  ): TreatmentOrNotAssignedOrNull<typeof experiment> {
    const overrides: { [key: string]: string } = this._experimentOverrides.getExperimentOverrides()
    const overrideGroup = overrides[experiment.name]
    if (overrideGroup && this.isValidTreatment(experiment, overrideGroup)) {
      return overrideGroup
    }
    return null
  }

  /**
   * Get gate override treatment
   */
  private getGateOverrideTreatment(gate: Gate): string | null {
    const overrides: { [key: string]: string } = this._experimentOverrides.getExperimentOverrides()
    const overrideGroup = overrides[gate.name]
    if (overrideGroup && this.isValidGateTreatment(gate, overrideGroup)) {
      return overrideGroup
    }
    return null
  }

  private trackAssignedTreatment<EX extends string>(
    experiment: Experiment<EX>,
    assignedGroup: TreatmentOrNotAssigned<typeof experiment>,
  ) {
    this._experimentsWithAssignedTreatment.set(experiment.name, assignedGroup)

    // Track pixel experiments for SSR pixel injection
    if (PixelExperiment.isPixelExperiment(experiment)) {
      this._pixelTracker.trackAssignment(experiment.name)
    }
  }

  /**
   * Get treatment for an experiment with assignment tracking
   */
  getTreatment = <EX extends string>(
    experiment: Experiment<EX>,
  ): TreatmentOrNotAssignedOrNull<typeof experiment> => {
    let assignedGroup = this.getOverrideTreatment(experiment)
    if (!assignedGroup) {
      // Pixel experiments will be assigned through pixel URL.
      // We don't want to call the assignment on the client-side for them, since it could introduce an assignment imbalance.
      // Hypothetical situation is when test and control group have performance difference and the client-side assignment won't be called equally across groups
      assignedGroup = PixelExperiment.isPixelExperiment(experiment)
        ? this._sdkExperimentClient?.getTreatmentWithExposureLoggingDisabled(experiment) || null
        : this._sdkExperimentClient?.getTreatment(experiment) || null
    }

    if (assignedGroup) {
      this.trackAssignedTreatment(experiment, assignedGroup)
    }

    return assignedGroup
  }

  /**
   * Peek at treatment for an experiment with assignment tracking
   */
  peekTreatment = <EX extends string>(
    experiment: Experiment<EX>,
  ): TreatmentOrNotAssignedOrNull<typeof experiment> => {
    return (
      this.getOverrideTreatment(experiment) ||
      this._sdkExperimentClient?.getTreatmentWithExposureLoggingDisabled(experiment) ||
      null
    )
  }

  /**
   * Check if gate is enabled with override support
   */
  isGateEnabled = (gate: Gate) => {
    const gateOverride = this.getGateOverrideTreatment(gate)
    if (gateOverride) {
      return gateOverride === gate.experience
    }

    return this._sdkExperimentClient?.isGateEnabled(gate)
  }
}
